import type { Result } from 'true-myth'

import { VALUE_SCHEMAS } from '@/lib/facts/values'
import type { Locale } from '@/i18n/routing'

import type { ValueFormatters } from './format'
import {
    attempt,
    formatIcu,
    formatValue,
    SENTINEL_PATTERN,
    sentinelIndex,
    type MessageFailure,
    type Variable,
} from './icu'
import type { StyleKind } from './variables'

/*
 * A message a client island formats against a value only the client knows — a selected
 * passenger count, a fetched trip's fare. The server hands the island the message's source
 * in one locale; the island renders it on each change through the ICU half of the grammar
 * the server renders by, and a template is never markdown: a client island parses none.
 */

/** The kinds a client template's values take: every value kind but an href, which means
 *  something only as a link target, and a link is markdown. */
export type TemplateKind = Exclude<StyleKind, 'href'>

export const isTemplateKind = (kind: StyleKind): kind is TemplateKind => kind !== 'href'

/** One locale's source of a store message, where it came from, so a failure names the
 *  file and key, and the kind of each argument its registry entry gives it. It carries no
 *  format: how a value is stated is the page's decision, which the island binds. */
export interface MessageTemplate {
    readonly locale: Locale
    readonly source: string
    readonly file: string
    readonly key: string
    readonly kinds: Readonly<Record<string, TemplateKind>>
}

/** A client template's values, each read as the kind its registry entry gives it. */
export type TemplateValues = Readonly<Record<string, unknown>>

/** A value of the wrong kind is the caller's defect, not the copy's, so it throws. */
function typed(kind: TemplateKind, name: string, value: unknown): Variable {
    if (kind === 'text') {
        if (typeof value !== 'string') {
            throw new TypeError(`{${name}} is text, not ${typeof value}`)
        }
        return { kind, value }
    }
    return { kind, value: VALUE_SCHEMAS[kind].parse(value) } as Variable
}

/** The template's text for the values, each formatted by its kind in the presentation its
 *  style names, by the formatters of the page's decision. */
export function renderTemplate(
    { locale, source, kinds }: MessageTemplate,
    values: TemplateValues,
    format: ValueFormatters,
): Result<string, MessageFailure> {
    // A value the caller holds none of is absent, as it is to a copy set's template.
    const variables = Object.fromEntries(
        Object.entries(values).flatMap(([name, value]) =>
            Object.hasOwn(kinds, name) && value !== undefined && value !== null
                ? [[name, typed(kinds[name], name, value)]]
                : [],
        ),
    )
    return attempt(() => {
        const { output, slots } = formatIcu(source, {
            locale,
            lookup: (name) =>
                Object.hasOwn(variables, name)
                    ? { status: 'present', variable: variables[name] }
                    : { status: 'absent' },
        })
        return output.replace(SENTINEL_PATTERN, (char) => {
            const { variable, presentation } = slots[sentinelIndex(char)]
            // Every sentinel left after the missing-variable check stands for a value.
            return formatValue(variable!, format, presentation)
        })
    })
}
